import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ArtifactForm } from "../../components/ArtifactTable";
import { Plus, Sparkles, Pencil, Trash2 } from "lucide-react";

const FMEA_FIELDS = [
  { key: "itemFunction", label: "Item / Function", type: "text" },
  { key: "potentialFailureMode", label: "Potential Failure Mode", type: "textarea" },
  { key: "potentialEffects", label: "Potential Effects", type: "textarea" },
  { key: "severity", label: "Severity (1-10)", type: "number" },
  { key: "potentialCauses", label: "Potential Causes", type: "textarea" },
  { key: "occurrence", label: "Occurrence (1-10)", type: "number" },
  { key: "currentControls", label: "Current Controls", type: "text" },
  { key: "detection", label: "Detection (1-10)", type: "number" },
  { key: "recommendedActions", label: "Recommended Actions", type: "textarea" },
];

function rpnTone(rpn) {
  if (rpn >= 200) return "red";
  if (rpn >= 100) return "amber";
  return "green";
}

export default function FmeaPage() {
  const { projectId } = useParams();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/fmea_rows`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/projects/${projectId}/singletons/fmea_meta`).then((r) => setMeta(r.data || {})).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...form };
    ["severity", "occurrence", "detection"].forEach((k) => (payload[k] = Number(payload[k] || 1)));
    payload.rpn = payload.severity * payload.occurrence * payload.detection;
    try {
      if (editing) await api.put(`/projects/${projectId}/artifacts/fmea_rows/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/artifacts/fmea_rows`, payload);
      setOpen(false); load(); toast.success("FMEA row saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const aiGenerate = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, {
        generationType: "fmea",
        context: { processOrProduct: meta.processOrProduct, scope: meta.scope, existingRows: rows.map((r) => ({ itemFunction: r.itemFunction, potentialFailureMode: r.potentialFailureMode })) },
      });
      const newRows = Array.isArray(data.result) ? data.result : [];
      for (const r of newRows) await api.post(`/projects/${projectId}/artifacts/fmea_rows`, r);
      toast.success(`${newRows.length} AI FMEA rows added`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setAiBusy(false);
  };

  return (
    <div data-testid="fmea-page">
      <PageHeader overline="Six Sigma Toolkit" title="FMEA"
        actions={
          <>
            <Button data-testid="ai-fmea-btn" variant="outline" className="rounded-none border-[#1E6FE8] text-[#1E6FE8]" onClick={aiGenerate} disabled={aiBusy}>
              <Sparkles className="h-4 w-4 mr-1" />{aiBusy ? "Working…" : "AI suggest rows"}
            </Button>
            <Button data-testid="add-fmea-btn" className="rounded-none" onClick={() => { setEditing(null); setForm({}); setOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" />Add Row
            </Button>
          </>
        }>
        <div className="text-xs text-muted-foreground mt-1 max-w-xl">{meta.processOrProduct} {meta.scope ? `· ${meta.scope}` : ""}</div>
      </PageHeader>

      <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-220px)]">
        <table className="w-full data-table text-sm" style={{ minWidth: 1100 }}>
          <thead>
            <tr>
              <th>Item / Function</th><th>Failure Mode</th><th>Effects</th><th>S</th>
              <th>Causes</th><th>O</th><th>Controls</th><th>D</th><th>Actions</th><th>RPN</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid="fmea-row">
                <td className="font-medium">{r.itemFunction}</td>
                <td>{r.potentialFailureMode}</td>
                <td>{r.potentialEffects}</td>
                <td className="text-center">{r.severity}</td>
                <td>{r.potentialCauses}</td>
                <td className="text-center">{r.occurrence}</td>
                <td>{r.currentControls}</td>
                <td className="text-center">{r.detection}</td>
                <td>{r.recommendedActions}</td>
                <td><StatusBadge value={r.rpn} tone={rpnTone(r.rpn)} /></td>
                <td className="whitespace-nowrap">
                  <button onClick={() => { setEditing(r); setForm(r); setOpen(true); }} className="p-1 hover:text-[#1E6FE8]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={async () => { await api.delete(`/projects/${projectId}/artifacts/fmea_rows/${r.id}`); load(); }} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} FMEA Row</DialogTitle></DialogHeader>
          <ArtifactForm fields={FMEA_FIELDS} value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-fmea-btn" className="rounded-none" onClick={save}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
